import React from "react";
import { motion, type MotionProps } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  BlockWrapper,
  type BlockWrapperProps,
  useSectionWrapperClasses,
} from "./BlockWrapper";

export type SectionWrapperVariant =
  | "none"
  | "narrow"
  | "default"
  | "wide"
  | "full"
  | "full-bleed";

const BASE_WRAPPER_PROPS: Partial<BlockWrapperProps> = {
  applyInternalSpacing: true,
  applyHorizontalPadding: true,
  applyVerticalPadding: false,
};

const WRAPPER_VARIANTS: Record<
  SectionWrapperVariant,
  Partial<BlockWrapperProps> | null
> = {
  none: null,
  narrow: { ...BASE_WRAPPER_PROPS, maxWidth: "5xl" },
  default: { ...BASE_WRAPPER_PROPS, maxWidth: "7xl" },
  wide: { ...BASE_WRAPPER_PROPS, maxWidth: "6xl" },
  full: { ...BASE_WRAPPER_PROPS, maxWidth: "full" },
  "full-bleed": {
    maxWidth: "full",
    applyInternalSpacing: false,
    applyHorizontalPadding: false,
    applyVerticalPadding: false,
  },
};

interface SectionShellProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  dir?: "ltr" | "rtl";
  role?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  motionProps?: MotionProps;
  applySectionPadding?: boolean;
  contentId?: string;
  contentRole?: string;
  contentClassName?: string;
  wrapperVariant?: SectionWrapperVariant;
  wrapperClassName?: string;
  wrapperProps?: Partial<BlockWrapperProps>;
  skipLink?: React.ReactNode;
  schema?: React.ReactNode;
  background?: React.ReactNode;
}

const SectionShell = ({
  id,
  children,
  className,
  style,
  dir,
  role = "region",
  ariaLabel,
  ariaLabelledBy,
  motionProps,
  applySectionPadding = true,
  contentId,
  contentRole,
  contentClassName,
  wrapperVariant = "none",
  wrapperClassName,
  wrapperProps,
  skipLink,
  schema,
  background,
}: SectionShellProps) => {
  const sectionPaddingClasses = useSectionWrapperClasses();
  const sectionClassName = cn(
    "relative overflow-hidden",
    applySectionPadding && sectionPaddingClasses,
    className
  );

  const wrapperConfig = WRAPPER_VARIANTS[wrapperVariant];

  const contentClassNames = cn("relative z-10", contentClassName);

  return (
    <>
      {schema}
      <motion.section
        id={id}
        className={sectionClassName}
        style={style}
        dir={dir}
        role={role}
        {...(ariaLabelledBy ? { "aria-labelledby": ariaLabelledBy } : {})}
        {...(!ariaLabelledBy && ariaLabel ? { "aria-label": ariaLabel } : {})}
        {...motionProps}
      >
        {skipLink}
        {background}
        {wrapperConfig ? (
          <BlockWrapper
            {...wrapperConfig}
            {...wrapperProps}
            id={contentId}
            role={contentRole}
            className={cn(
              wrapperConfig.className,
              wrapperClassName,
              wrapperProps?.className,
              contentClassNames
            )}
          >
            {children}
          </BlockWrapper>
        ) : (
          <div
            id={contentId}
            role={contentRole}
            className={contentClassNames}
          >
            {children}
          </div>
        )}
      </motion.section>
    </>
  );
};

export default SectionShell;
